import PaymentIconImage from "@/components/Images/PaymentIconImage";
import { PAYMENT_METHOD_ID } from "@/lib/constants";
import formStyles from "@/styles/Form.module.scss";
import styles from "@/styles/PaymentSelect.module.scss";
import classnames from "classnames";
import { useCallback, useMemo } from "react";
import { IGNORE_CARRIER, IGNORE_PAYPAY, PURCHASE_MODE } from "./index";
import PaymentRadioButton from "./PaymentRadioButton";

export default function PaymentMethodsList({
  mode,
  paymentMethods,
  paymentInstruments,
  register,
  onChange,
}) {
  if (!paymentMethods) return null;
  const purchase = PURCHASE_MODE.includes(mode);
  const paymentRegistration = register("paymentMethod");

  // 登録済みの支払い方法
  const registeredPaymentMethodIds = useMemo(() => {
    if (!paymentInstruments) return [];
    return paymentInstruments.map((payment) => payment.payment_method_id);
  }, [paymentInstruments]);
  // 支払い方法ごとに分ける
  const creditCard = useMemo(() => {
    return paymentMethods.filter(
      (v) => v.payment_method_id === PAYMENT_METHOD_ID.CREDIT_CARD_3DS
    );
  }, [paymentMethods]);

  const paypay = useMemo(() => {
    return paymentMethods.filter(
      (v) =>
        !IGNORE_PAYPAY.includes(mode) &&
        !registeredPaymentMethodIds.includes(v.payment_method_id) &&
        v.payment_method_id === PAYMENT_METHOD_ID.PAYPAY
    );
  }, [mode, paymentMethods, registeredPaymentMethodIds]);

  const carrier = useMemo(() => {
    return paymentMethods.filter(
      (v) =>
        !IGNORE_CARRIER.includes(mode) &&
        !registeredPaymentMethodIds.includes(v.payment_method_id) &&
        [
          PAYMENT_METHOD_ID.AU,
          PAYMENT_METHOD_ID.DOCOMO,
          PAYMENT_METHOD_ID.SOFTBANK,
        ].includes(v.payment_method_id)
    );
  }, [mode, paymentMethods, registeredPaymentMethodIds]);

  const labelClassName = useCallback((v) => {
    return classnames(styles.radioCheck, styles.paymentMethodIcon, {
      [styles.creditCard]:
        v.payment_method_id === PAYMENT_METHOD_ID.CREDIT_CARD_3DS ||
        v.payment_method_id === PAYMENT_METHOD_ID.CREDIT_CARD_NO3DS,
      [styles.paypay]: v.payment_method_id === PAYMENT_METHOD_ID.PAYPAY,
      [styles.docomo]: v.payment_method_id === PAYMENT_METHOD_ID.DOCOMO,
      [styles.softbank]: v.payment_method_id === PAYMENT_METHOD_ID.SOFTBANK,
      [styles.au]: v.payment_method_id == PAYMENT_METHOD_ID.AU,
    });
  }, []);

  const handleChange = useCallback(
    (e) => {
      paymentRegistration.onChange(e);
      const paymentMethodId = Number(e.target.value);
      onChange({
        paymentMethod: paymentMethodId,
        createPayment: true,
        showInput:
          paymentMethodId === PAYMENT_METHOD_ID.CREDIT_CARD_3DS ||
          paymentMethodId === PAYMENT_METHOD_ID.CREDIT_CARD_NO3DS,
      });
    },
    [onChange, paymentRegistration]
  );

  return (
    <fieldset
      className={`${formStyles.marginOff} ${styles.paymentMethodsListFieldset}`}
    >
      <p className={styles.paymentInstrumentsListFieldsetTitle}>
        {purchase && "その他のお支払い方法を利用する"}
        {!purchase && "新しく追加するお支払い方法"}
      </p>
      <div className={styles.paymentInstrumentStyle}>
        {creditCard && (
          <>
            {creditCard.map((v) => (
              <PaymentRadioButton
                key={`paymentMethod-${v.payment_method_id}`}
                {...paymentRegistration}
                onChange={handleChange}
                paymentMethodId={v.payment_method_id}
                id={`paymentMethodId${v.payment_method_id}`}
                className={labelClassName(v)}
                value={v.payment_method_id}
              >
                <div className={styles.iconWithText}>
                  <div className={styles.cardImage}>
                    <PaymentIconImage
                      large
                      paymentMethodId={PAYMENT_METHOD_ID.CREDIT_CARD_3DS}
                      cardType="all"
                    />
                  </div>
                  <span className={styles.paymentCreditCardText}>
                    各種クレジットカード
                  </span>
                </div>
              </PaymentRadioButton>
            ))}
          </>
        )}
        {paypay && (
          <>
            {paypay.map((v) => (
              <PaymentRadioButton
                key={`paymentMethod-${v.payment_method_id}`}
                {...paymentRegistration}
                onChange={handleChange}
                paymentMethodId={v.payment_method_id}
                id={`paymentMethodId${v.payment_method_id}`}
                className={labelClassName(v)}
                value={v.payment_method_id}
              >
                <PaymentIconImage large paymentMethodId={v.payment_method_id} />
              </PaymentRadioButton>
            ))}
          </>
        )}
        {carrier && (
          <div className={styles.carrierPayments}>
            {carrier.map((v) => (
              <PaymentRadioButton
                key={`paymentMethod-${v.payment_method_id}`}
                {...paymentRegistration}
                onChange={handleChange}
                paymentMethodId={v.payment_method_id}
                id={`paymentMethodId${v.payment_method_id}`}
                className={labelClassName(v)}
                value={v.payment_method_id}
              >
                <PaymentIconImage large paymentMethodId={v.payment_method_id} />
              </PaymentRadioButton>
            ))}
          </div>
        )}
        {!IGNORE_CARRIER.includes(mode) && purchase && (
          <p className={styles.caution}>
            キャリア決済を新規でご登録いただく際、各キャリアのページでお手続き後に商品購入確認画面に戻ってくる場合がございますので、手続を中断されぬようお気をつけください。
          </p>
        )}
        {IGNORE_CARRIER.includes(mode) && (
          <div className={formStyles.caption}>
            新規での商品購入時の【キャリア決済】の対応を終了いたしましたので、お支払方法として追加はできません
          </div>
        )}
      </div>
    </fieldset>
  );
}
